const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const modulo = (value, length) => ((value % length) + length) % length;
const themeToken = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

function initPixelText(root = document) {
  root.querySelectorAll("[data-pixel-text]").forEach((element) => {
    if (element.dataset.ready) return;
    element.dataset.ready = "true";
    const canvas = element.querySelector("canvas");
    const label = element.querySelector("[data-pixel-label]");
    const context = canvas?.getContext("2d");
    if (!canvas || !label || !context) return;

    let frame = 0;
    const draw = () => {
      frame = 0;
      const { width, height } = canvas.getBoundingClientRect();
      const safeWidth = Math.max(width, 1);
      const safeHeight = Math.max(height, 1);
      const ratio = window.devicePixelRatio || 1;
      const align = element.dataset.align || "center";
      const text = label.textContent || "";
      const font = themeToken("--font-propal3-pixel");
      const measure = document.createElement("canvas").getContext("2d");
      let fontSize = safeHeight;
      if (measure && element.dataset.fit !== "false") {
        measure.font = `900 ${fontSize}px ${font}`;
        fontSize *= Math.min((safeWidth * 0.96) / Math.max(measure.measureText(text).width, 1), 1);
      }

      const texture = document.createElement("canvas");
      const textureContext = texture.getContext("2d");
      canvas.width = Math.ceil(safeWidth * ratio);
      canvas.height = Math.ceil(safeHeight * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, safeWidth, safeHeight);
      if (!textureContext) return;

      const scale = 0.7;
      texture.width = Math.ceil(safeWidth * scale);
      texture.height = Math.ceil(safeHeight * scale);
      textureContext.scale(scale, scale);
      textureContext.fillStyle = themeToken(`--color-${element.dataset.color || "signal"}`);
      textureContext.textAlign = align;
      textureContext.textBaseline = "middle";
      textureContext.font = `900 ${fontSize}px ${font}`;
      textureContext.fillText(text, align === "left" ? 0 : align === "right" ? safeWidth : safeWidth / 2, safeHeight / 2);
      context.imageSmoothingEnabled = false;
      context.drawImage(texture, 0, 0, safeWidth, safeHeight);
    };
    const requestDraw = () => {
      if (!frame) frame = requestAnimationFrame(draw);
    };
    element.pixelDraw = requestDraw;
    new ResizeObserver(requestDraw).observe(canvas);
    document.fonts.ready.then(requestDraw);
    requestDraw();
  });
}

function setPixelText(element, text, color) {
  const label = element?.querySelector("[data-pixel-label]");
  if (!label) return;
  label.textContent = text;
  if (color) element.dataset.color = color;
  element.pixelDraw?.();
}

function initHero(root) {
  const hero = root.querySelector("[data-hero]");
  if (!hero || hero.dataset.ready) return;
  hero.dataset.ready = "true";
  const wrapper = hero.querySelector("[data-hero-text]");
  const canvas = wrapper?.querySelector("canvas");
  const label = wrapper?.querySelector("[data-hero-label]");
  const video = hero.querySelector("video");
  const context = canvas?.getContext("2d");
  const lines = ["LA FEVE", "ZENITH TOUR", "ALBUM COMING SOON"];
  const heights = ["h-0", "h-propal3-hero-text-mobile-1", "h-propal3-hero-text-mobile-2", "h-propal3-hero-text-mobile"];
  const desktopHeights = ["", "md:h-propal3-hero-text-1", "md:h-propal3-hero-text-2", "md:h-propal3-hero-text"];
  let step = 0;

  const draw = () => {
    if (!canvas || !context || !wrapper) return;
    const { width, height } = canvas.getBoundingClientRect();
    const safeWidth = Math.max(width, 1);
    const safeHeight = Math.max(height, 1);
    const ratio = window.devicePixelRatio || 1;
    const font = themeToken("--font-propal3-pixel");
    const visible = lines.slice(0, step);
    let fontSize = clamp(width * 0.14, 34, 122);
    const measure = document.createElement("canvas").getContext("2d");
    if (measure && visible.length) {
      measure.font = `900 ${fontSize}px ${font}`;
      const widest = visible.reduce((value, line) => Math.max(value, measure.measureText(line).width), 1);
      fontSize *= Math.min((safeWidth * 0.94) / widest, safeHeight / Math.max(visible.length * fontSize, fontSize), 1);
    }
    const texture = document.createElement("canvas");
    const textureContext = texture.getContext("2d");
    canvas.width = Math.ceil(safeWidth * ratio);
    canvas.height = Math.ceil(safeHeight * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, safeWidth, safeHeight);
    if (!textureContext || !visible.length) return;
    const scale = 0.42;
    texture.width = Math.ceil(safeWidth * scale);
    texture.height = Math.ceil(safeHeight * scale);
    textureContext.scale(scale, scale);
    textureContext.fillStyle = themeToken("--color-signal");
    textureContext.textBaseline = "top";
    textureContext.font = `900 ${fontSize}px ${font}`;
    visible.forEach((line, index) => textureContext.fillText(line, 0, index * fontSize));
    context.imageSmoothingEnabled = false;
    context.drawImage(texture, 0, 0, safeWidth, safeHeight);
  };

  const showStep = (nextStep) => {
    step = nextStep;
    wrapper.classList.remove(...heights, ...desktopHeights.filter(Boolean));
    wrapper.classList.add(heights[step]);
    if (desktopHeights[step]) wrapper.classList.add(desktopHeights[step]);
    video?.classList.toggle("mb-4", step > 0);
    label.innerHTML = lines.slice(0, step).map((line) => `<p>${line}</p>`).join("");
    requestAnimationFrame(draw);
  };

  const skip = sessionStorage.getItem("skip-la-feve-intro") === "true";
  if (skip) {
    sessionStorage.removeItem("skip-la-feve-intro");
    hero.classList.add("pointer-events-none", "opacity-0");
    return;
  }
  [1, 2, 3].forEach((nextStep) => setTimeout(() => showStep(nextStep), nextStep * 1000));
  setTimeout(() => hero.classList.add("pointer-events-none", "opacity-0"), 4500);
  window.addEventListener("resize", draw);
}

function initShop(root) {
  const shop = root.matches?.("[data-shop]") ? root : root.querySelector("[data-shop]");
  if (!shop || shop.dataset.ready) return;
  shop.dataset.ready = "true";
  initHero(shop);
  const canvas = shop.querySelector("[data-shop-canvas]");
  const scroller = shop.querySelector("[data-shop-scroll]");
  const footerTitle = shop.querySelector("[data-shop-title]");
  const footerPrice = shop.querySelector("[data-shop-price]");
  const footerTitleText = footerTitle?.querySelector("[data-pixel-text]");
  const footerPriceText = footerPrice?.querySelector("[data-pixel-text]");
  const products = [...shop.querySelectorAll("[data-shop-products] li")].map((item) => ({ ...item.dataset }));
  if (!canvas || !scroller || !products.length) return;

  const context = canvas.getContext("2d", { alpha: false, colorSpace: "srgb" });
  if (!context) return;
  const size = 1024;
  const loopHeight = products.length * size;
  let width = 1;
  let height = 1;
  let virtualScroll = 0;
  let lastScroll = 0;
  let frame = 0;
  let activeIndex = -1;
  let touchY = 0;
  let touchTime = 0;
  let touchVelocity = 0;
  let momentumFrame = 0;
  let suppressClick = false;
  const textures = products.map(() => document.createElement("canvas"));
  const textureReady = products.map(() => false);
  const images = products.map((product) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = product.image;
    return image;
  });

  const buildTexture = (index) => {
    const image = images[index];
    const texture = textures[index];
    const textureContext = texture.getContext("2d");
    if (!textureContext || !image.naturalWidth) return;
    texture.width = size;
    texture.height = size;
    const scale = Math.min(size / image.naturalWidth, size / image.naturalHeight);
    const imageWidth = image.naturalWidth * scale;
    const imageHeight = image.naturalHeight * scale;
    textureContext.imageSmoothingEnabled = true;
    textureContext.imageSmoothingQuality = "high";
    textureContext.drawImage(image, (size - imageWidth) / 2, (size - imageHeight) / 2, imageWidth, imageHeight);
    textureReady[index] = true;
    requestDraw();
  };

  const updateLabel = () => {
    const index = Math.floor(modulo(virtualScroll + size / 2, loopHeight) / size);
    if (index === activeIndex) return;
    activeIndex = index;
    setPixelText(footerTitleText, products[index].title);
    setPixelText(footerPriceText, products[index].price);
  };
  const draw = () => {
    frame = 0;
    context.fillStyle = themeToken("--color-stage");
    context.fillRect(0, 0, width, height);
    if (textureReady.some((ready) => !ready)) return;
    const compact = width < 720;
    const centerWidth = compact ? 0.68 : 0.34;
    const depthWidth = compact ? 0.4 : 0.38;
    const flatHalf = compact ? 0.46 : 0.68;
    const sourceScale = height / (2 * products.length * centerWidth * width);
    const sourceStep = size / (centerWidth * width);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    for (let y = -2; y < height + 2; y += 2) {
      const center = (y - height / 2) / (height / 2);
      const depth = clamp((Math.abs(center) - flatHalf) / (1 - flatHalf), 0, 1);
      const tunnelWidth = centerWidth + depth * depthWidth;
      const sourceY = modulo(virtualScroll + size / 2 + center * loopHeight * sourceScale, loopHeight);
      const texture = textures[Math.floor(sourceY / size)];
      const localY = sourceY % size;
      const drawWidth = width * tunnelWidth;
      const sourceHeight = Math.min(sourceStep * 2.25, size - localY);
      context.drawImage(texture, 0, localY, size, sourceHeight, width / 2 - drawWidth / 2, y, drawWidth, 2.25);
    }
  };
  const requestDraw = () => { if (!frame) frame = requestAnimationFrame(draw); };
  const resize = () => {
    width = window.innerWidth;
    height = window.innerHeight;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.ceil(width * ratio);
    canvas.height = Math.ceil(height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    requestDraw();
  };
  const productAt = (clientY) => {
    const center = (clientY - canvas.getBoundingClientRect().top - height / 2) / (height / 2);
    const centerWidth = width < 720 ? 0.68 : 0.34;
    const source = modulo(virtualScroll + size / 2 + center * loopHeight * (height / (2 * products.length * centerWidth * width)), loopHeight);
    return products[Math.floor(source / size)];
  };
  images.forEach((image, index) => {
    image.addEventListener("load", () => buildTexture(index));
    if (image.complete && image.naturalWidth) buildTexture(index);
  });
  const centerScroller = () => {
    scroller.scrollTop = scroller.scrollHeight / 2;
    lastScroll = scroller.scrollTop;
  };
  const addScroll = (delta) => {
    virtualScroll = modulo(virtualScroll + delta, loopHeight);
    updateLabel();
    requestDraw();
  };
  const stopMomentum = () => {
    if (momentumFrame) cancelAnimationFrame(momentumFrame);
    momentumFrame = 0;
  };
  const startMomentum = () => {
    let previous = performance.now();
    const tick = (now) => {
      const elapsed = Math.max(now - previous, 1);
      addScroll(touchVelocity * elapsed * 2.2);
      touchVelocity *= Math.pow(0.965, elapsed / 16.67);
      previous = now;
      momentumFrame = Math.abs(touchVelocity) > 0.02 ? requestAnimationFrame(tick) : 0;
    };
    momentumFrame = requestAnimationFrame(tick);
  };
  centerScroller();
  scroller.addEventListener("scroll", () => {
    const next = scroller.scrollTop;
    addScroll((next - lastScroll) * 2.2);
    lastScroll = next;
  }, { passive: true });
  scroller.addEventListener("touchstart", (event) => {
    stopMomentum();
    touchY = event.touches[0]?.clientY || 0;
    touchTime = performance.now();
    touchVelocity = 0;
    suppressClick = false;
  }, { passive: true });
  scroller.addEventListener("touchmove", (event) => {
    const y = event.touches[0]?.clientY ?? touchY;
    const now = performance.now();
    const delta = touchY - y;
    event.preventDefault();
    if (Math.abs(delta) > 1) suppressClick = true;
    touchVelocity = clamp(delta / Math.max(now - touchTime, 1), -4.8, 4.8);
    addScroll(delta * 2.2);
    touchY = y;
    touchTime = now;
  }, { passive: false });
  scroller.addEventListener("touchend", () => {
    if (Math.abs(touchVelocity) > 0.02) startMomentum();
    setTimeout(() => { suppressClick = false; }, 0);
  }, { passive: true });
  scroller.addEventListener("touchcancel", stopMomentum, { passive: true });
  scroller.addEventListener("click", (event) => {
    if (suppressClick) {
      suppressClick = false;
      return;
    }
    const product = productAt(event.clientY);
    if (product?.url && product.url !== "#") location.href = product.url;
  });
  scroller.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    const product = productAt(height / 2);
    if (product?.url && product.url !== "#") location.href = product.url;
  });
  window.addEventListener("resize", resize);
  window.addEventListener("pagehide", stopMomentum);
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    centerScroller();
    resize();
  });
  resize();
  updateLabel();
  requestDraw();
}

function initCylinder(canvas, entries, mode, onSelect) {
  if (!canvas || !entries.length || canvas.dataset.ready) return;
  canvas.dataset.ready = "true";
  const context = canvas.getContext("2d");
  if (!context) return;
  const live = mode === "live";
  let texture;
  let textureContext;
  let loopHeight = 1;
  let rowHeight = 1;
  let sourceScale = 1;
  let width = 1;
  let height = 1;
  let scroll = 0;
  let frame = 0;
  let lastTouchY = 0;
  let lastTouchTime = 0;
  let velocity = 0;
  let momentum = 0;

  const build = () => {
    width = window.innerWidth;
    height = window.innerHeight;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.ceil(width * ratio);
    canvas.height = Math.ceil(height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    const compact = width < 720;
    const fontSize = live
      ? compact ? clamp(width * 0.22, 92, 136) : clamp(width * 0.095, 52, 156)
      : compact ? clamp(width * 0.26, 84, 150) : clamp(width * 0.16, 112, 270);
    rowHeight = fontSize * (live ? 0.84 : 0.86);
    loopHeight = entries.length * rowHeight;
    sourceScale = clamp(loopHeight / height, live ? 0.9 : 0.52, live ? 1.55 : 1.18);
    scroll = modulo(scroll, loopHeight);
    const measure = document.createElement("canvas").getContext("2d");
    if (!measure) return;
    measure.font = `900 ${fontSize}px ${themeToken("--font-propal3-pixel")}`;
    const widest = entries.reduce((value, entry) => Math.max(value, measure.measureText(entry.text).width), 0);
    const textureWidth = Math.max(width * (live ? 3.6 : 3.8), widest * (live ? 2.1 : 1.55), live ? 1800 : 1600);
    const copies = live ? 4 : 5;
    texture = document.createElement("canvas");
    texture.width = Math.ceil(textureWidth);
    texture.height = Math.ceil(loopHeight * copies);
    textureContext = texture.getContext("2d");
    if (!textureContext) return;
    textureContext.fillStyle = themeToken("--color-signal");
    textureContext.textAlign = "center";
    textureContext.textBaseline = "middle";
    textureContext.font = `900 ${fontSize}px ${themeToken("--font-propal3-pixel")}`;
    if (!live) {
      textureContext.shadowColor = themeToken("--color-signal");
      textureContext.shadowBlur = compact ? 10 : 18;
    }
    for (let copy = 0; copy < copies; copy += 1) {
      entries.forEach((entry, index) => textureContext.fillText(entry.text, textureWidth / 2, (copy * entries.length + index + 0.5) * rowHeight));
    }
  };
  const draw = () => {
    frame = 0;
    if (!textureContext) return;
    context.fillStyle = themeToken("--color-stage");
    context.fillRect(0, 0, width, height);
    for (let y = -2; y < height + 2; y += 2) {
      const center = (y - height / 2) / (height / 2);
      const bend = Math.sign(center) * Math.pow(Math.abs(center), live ? 1.42 : 1.56);
      const depth = Math.pow(Math.abs(center), live ? 1.8 : 1.92);
      const compact = width < 720;
      const clipWide = !live && canvas.dataset.clipWide === "true";
      const widthScale = live
        ? compact ? 0.64 + depth * 1.16 : 0.3 + depth * 1.6
        : compact ? (clipWide ? 0.72 + depth * 1.48 : 0.5 + depth * 1.7) : (clipWide ? 0.4 + depth * 2.19 : 0.24 + depth * 2.35);
      const sourceY = modulo(scroll + loopHeight + height * 0.5 + bend * height * sourceScale, loopHeight);
      const drawWidth = width * widthScale;
      context.globalAlpha = live ? 0.58 + depth * 0.42 : 0.72 + depth * 0.28;
      context.drawImage(texture, 0, loopHeight + sourceY, texture.width, 2, width / 2 - drawWidth / 2, y, drawWidth, 2.35);
    }
    context.globalAlpha = 1;
  };
  const requestDraw = () => { if (!frame) frame = requestAnimationFrame(draw); };
  const addScroll = (delta) => { scroll = modulo(scroll + delta, loopHeight); requestDraw(); };
  const stopMomentum = () => { if (momentum) cancelAnimationFrame(momentum); momentum = 0; };
  const startMomentum = () => {
    let last = performance.now();
    const tick = () => {
      const now = performance.now();
      const elapsed = Math.max(now - last, 1);
      addScroll(velocity * elapsed * (live ? 1.35 : 1.32));
      velocity *= Math.pow(live ? 0.975 : 0.976, elapsed / 16.67);
      last = now;
      momentum = Math.abs(velocity) > 0.02 ? requestAnimationFrame(tick) : 0;
    };
    momentum = requestAnimationFrame(tick);
  };
  window.addEventListener("wheel", (event) => { event.preventDefault(); addScroll(event.deltaY * (live ? 0.82 : 0.9)); }, { passive: false });
  window.addEventListener("touchstart", (event) => { stopMomentum(); lastTouchY = event.touches[0]?.clientY || 0; lastTouchTime = performance.now(); velocity = 0; }, { passive: true });
  window.addEventListener("touchmove", (event) => {
    const y = event.touches[0]?.clientY ?? lastTouchY;
    const now = performance.now();
    const delta = lastTouchY - y;
    event.preventDefault();
    velocity = clamp(delta / Math.max(now - lastTouchTime, 1), live ? -4.5 : -4.8, live ? 4.5 : 4.8);
    addScroll(delta * (live ? 1.08 : 1.1));
    lastTouchY = y;
    lastTouchTime = now;
  }, { passive: false });
  window.addEventListener("touchend", () => { if (Math.abs(velocity) > 0.02) startMomentum(); });
  window.addEventListener("keydown", (event) => {
    if (["ArrowDown", "PageDown", " "].includes(event.key)) { event.preventDefault(); addScroll(120); }
    if (["ArrowUp", "PageUp"].includes(event.key)) { event.preventDefault(); addScroll(-120); }
  });
  window.addEventListener("resize", () => { build(); requestDraw(); });
  if (onSelect) canvas.addEventListener("click", (event) => {
    const center = (event.clientY - canvas.getBoundingClientRect().top - height / 2) / (height / 2);
    const bend = Math.sign(center) * Math.pow(Math.abs(center), 1.42);
    const source = modulo(scroll + loopHeight + height * 0.5 + bend * height * sourceScale, loopHeight);
    onSelect(entries[Math.floor(source / rowHeight) % entries.length]);
  });
  document.fonts.ready.then(() => { build(); requestDraw(); });
  build();
  requestDraw();
}

function initLive(root) {
  const live = root.matches?.("[data-live]") ? root : root.querySelector("[data-live]");
  if (!live || live.dataset.ready) return;
  live.dataset.ready = "true";
  sessionStorage.setItem("skip-la-feve-intro", "true");
  const entries = [...live.querySelectorAll("[data-live-dates] li")].map((item) => ({ text: `${item.dataset.date} ${item.dataset.city}`, url: item.dataset.url }));
  initCylinder(live.querySelector("[data-live-canvas]"), entries, "live", (entry) => entry?.url && window.open(entry.url, "_blank", "noopener,noreferrer"));
}

function initClip(root) {
  const clip = root.matches?.("[data-clip]") ? root : root.querySelector("[data-clip]");
  if (!clip || clip.dataset.ready) return;
  clip.dataset.ready = "true";
  const choices = [...clip.querySelectorAll("[data-clip-choice]")];
  const videos = [...clip.querySelectorAll("[data-clip-video]")];
  const picker = clip.querySelector("[data-clip-picker]");
  const player = clip.querySelector("[data-clip-player]");
  const heading = clip.querySelector("[data-clip-heading]");
  const unmute = clip.querySelector("[data-unmute]");
  const canvas = clip.querySelector("[data-clip-canvas]");
  if (!choices.length || !videos.length || !picker || !player || !unmute || !canvas) return;

  const play = (video) => {
    videos.forEach((item) => {
      item.pause();
      item.hidden = item !== video;
    });
    unmute.classList.add("hidden");
    video.currentTime = 0;
    video.muted = false;
    video.play().catch(() => {
      video.muted = true;
      video.play().catch(() => {});
      unmute.classList.remove("hidden");
    });
  };

  choices.forEach((choice) => choice.addEventListener("click", () => {
    const video = videos.find((item) => item.dataset.clipVideo === choice.dataset.clipChoice);
    if (!video) return;
    picker.classList.add("hidden");
    player.classList.remove("hidden");
    player.classList.add("flex");
    if (heading) heading.textContent = choice.dataset.clipTitle;
    canvas.dataset.clipWide = choice.dataset.clipChoice === "2026";
    canvas.classList.remove("hidden");
    initCylinder(canvas, [{ text: choice.dataset.clipTitle }], "clip");
    play(video);
  }));

  unmute.addEventListener("click", () => {
    const video = videos.find((item) => !item.hidden);
    if (!video) return;
    video.muted = false;
    video.play().catch(() => {});
    unmute.classList.add("hidden");
  });
}

function initProduct(root) {
  const product = root.matches?.("[data-product]") ? root : root.querySelector("[data-product]");
  if (!product || product.dataset.ready) return;
  product.dataset.ready = "true";
  sessionStorage.setItem("skip-la-feve-intro", "true");
  const input = product.querySelector("[data-variant-id]");
  const groups = [...product.querySelectorAll("[data-product-option]")];
  const imageGroupIndex = groups.findIndex((group) => group.hasAttribute("data-image-option"));
  const buttons = [...product.querySelectorAll("[data-option-value]")];
  const variants = [...product.querySelectorAll("[data-product-variants] li")].map((item) => ({
    ...item.dataset,
    options: JSON.parse(item.dataset.options),
  }));
  const price = product.querySelector("[data-product-price]");
  const priceText = price?.querySelector("[data-pixel-text]");
  const image = product.querySelector("[data-product-image] img");
  const quantity = product.querySelector("[data-quantity]");
  const addButton = product.querySelector("[data-add-to-cart]");
  const addLabel = addButton?.querySelector("[data-add-label]");
  const drawSwatch = (canvas) => {
    const context = canvas.getContext("2d");
    const selected = canvas.parentElement.getAttribute("aria-pressed") === "true";
    canvas.width = Math.max(canvas.clientWidth, 1);
    canvas.height = Math.max(canvas.clientHeight, 1);
    context.fillStyle = canvas.dataset.colorSwatch;
    context.fillRect(0, 0, canvas.width, canvas.height);
    if (!selected) return;
    context.strokeStyle = themeToken("--color-signal");
    context.lineWidth = Math.max(canvas.width * 0.04, 2);
    context.strokeRect(context.lineWidth / 2, context.lineWidth / 2, canvas.width - context.lineWidth, canvas.height - context.lineWidth);
  };
  const update = () => {
    const selected = groups.map((group) => group.querySelector("[aria-pressed='true']")?.dataset.optionValue);
    const variant = groups.length ? variants.find((item) => item.options.every((value, index) => value === selected[index])) : variants[0];
    if (variant) {
      input.value = variant.id;
      if (addButton) addButton.disabled = variant.available !== "true";
      if (addLabel) addLabel.textContent = variant.available === "true" ? "ADD TO CART" : "SOLD OUT";
      if (priceText) setPixelText(priceText, variant.price);
      const matchingImage = imageGroupIndex < 0 ? null : variants.find((item) => item.image && item.options[imageGroupIndex] === selected[imageGroupIndex]);
      const nextImage = variant.image || matchingImage?.image;
      if (image && nextImage) {
        image.removeAttribute("srcset");
        image.src = nextImage;
      }
    }
    buttons.forEach((button) => {
      const groupIndex = groups.indexOf(button.closest("[data-product-option]"));
      const isAvailable = variants.some((item) => item.available === "true" && item.options[groupIndex] === button.dataset.optionValue && item.options.slice(0, groupIndex).every((value, index) => value === selected[index]));
      const text = button.querySelector("[data-pixel-text]");
      const isSelected = button.getAttribute("aria-pressed") === "true" && isAvailable;
      button.disabled = !isAvailable;
      button.setAttribute("aria-label", `${button.dataset.optionLabel}${isAvailable ? "" : ", épuisée"}`);
      button.querySelector("[data-sold-out]")?.classList.toggle("hidden", isAvailable);
      if (text) {
        button.classList.toggle("bg-signal", isSelected);
        button.classList.toggle("bg-stage", !isSelected);
        setPixelText(text, text.querySelector("[data-pixel-label]").textContent, isSelected ? "stage" : "signal");
      }
      const swatch = button.querySelector("[data-color-swatch]");
      if (swatch) drawSwatch(swatch);
    });
  };
  buttons.forEach((button) => button.addEventListener("click", () => {
    const group = button.closest("[data-product-option]");
    group.querySelectorAll("[data-option-value]").forEach((item) => item.setAttribute("aria-pressed", item === button));
    update();
  }));
  const changeQuantity = (delta) => { quantity.value = Math.max((Number.parseInt(quantity.value, 10) || 1) + delta, 1); };
  product.querySelector("[data-quantity-minus]")?.addEventListener("click", () => changeQuantity(-1));
  product.querySelector("[data-quantity-plus]")?.addEventListener("click", () => changeQuantity(1));
  quantity?.addEventListener("change", () => changeQuantity(0));
  new ResizeObserver(() => product.querySelectorAll("[data-color-swatch]").forEach(drawSwatch)).observe(product);
  update();
}

function boot(root = document) {
  initPixelText(root);
  initShop(root);
  initLive(root);
  initClip(root);
  initProduct(root);
}

boot();
document.addEventListener("shopify:section:load", (event) => boot(event.target));
